import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './BotzNav.css';

export const BOTZ_SECTIONS = [
  { id: 'quiz-heading', label: 'Quiz', hint: 'Daily cinema questions', icon: 'Q' },
  { id: 'song-heading', label: 'Song', hint: 'Guess the song', icon: '♪' },
  { id: 'tweet-heading', label: 'Tweet', hint: 'Top cinema tweets', icon: '𝕏' },
  { id: 'news-heading', label: 'News', hint: 'Latest film news', icon: 'N' },
  { id: 'reels-heading', label: 'Reels', hint: 'Trending reels', icon: '▶' },
  { id: 'memes-heading', label: 'Meme', hint: 'Top meme photos', icon: '☺' },
];

const LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy', hint: 'Privacy policy', icon: 'P' },
  { to: '/terms', label: 'Terms', hint: 'Terms of use', icon: 'T' },
];

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function BotzNav() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(BOTZ_SECTIONS[0].id);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const close = useCallback(() => setOpen(false), []);

  const goToSection = useCallback((id) => {
    close();
    if (isHome) {
      scrollToSection(id);
      window.history.replaceState(null, '', `#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  }, [close, isHome, navigate]);

  useEffect(() => {
    if (!isHome) return undefined;

    if (location.hash) {
      const id = location.hash.slice(1);
      if (BOTZ_SECTIONS.some((s) => s.id === id)) {
        requestAnimationFrame(() => scrollToSection(id));
      }
    }

    const sectionEls = BOTZ_SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);

    if (sectionEls.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0, 0.25, 0.5] }
    );

    sectionEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHome, location.hash]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    const onPointerDown = (e) => {
      if (
        panelRef.current?.contains(e.target) ||
        triggerRef.current?.contains(e.target)
      ) {
        return;
      }
      close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  return (
    <nav className="botz-nav" aria-label="Quick launch menu">
      <button
        ref={triggerRef}
        type="button"
        className={`botz-nav-trigger${open ? ' botz-nav-trigger--open' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="botz-nav-panel"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="botz-nav-trigger-icon" aria-hidden>
          <span /><span /><span /><span />
        </span>
        <span className="botz-nav-trigger-label">Botz</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          id="botz-nav-panel"
          className="botz-nav-panel"
          role="menu"
        >
          <p className="botz-nav-panel-title">Jump to</p>
          <ul className="botz-nav-list">
            {BOTZ_SECTIONS.map((section) => (
              <li key={section.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={`botz-nav-item${
                    isHome && activeId === section.id ? ' botz-nav-item--active' : ''
                  }`}
                  onClick={() => goToSection(section.id)}
                >
                  <span className="botz-nav-item-icon" aria-hidden>{section.icon}</span>
                  <span className="botz-nav-item-text">
                    <span className="botz-nav-item-label">{section.label}</span>
                    <span className="botz-nav-item-hint">{section.hint}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="botz-nav-divider" role="separator" />

          <p className="botz-nav-panel-title">Legal</p>
          <ul className="botz-nav-list">
            {LEGAL_LINKS.map((link) => (
              <li key={link.to} role="none">
                <Link
                  to={link.to}
                  role="menuitem"
                  className={`botz-nav-item${
                    location.pathname === link.to ? ' botz-nav-item--active' : ''
                  }`}
                  onClick={close}
                >
                  <span className="botz-nav-item-icon" aria-hidden>{link.icon}</span>
                  <span className="botz-nav-item-text">
                    <span className="botz-nav-item-label">{link.label}</span>
                    <span className="botz-nav-item-hint">{link.hint}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
}

export default BotzNav;
